"use client";

import {
    useState,
    useEffect,
    useMemo
} from "react";
import {
    Card,
    Form,
    Table
} from "react-bootstrap";
import {
    useNavigate
} from "react-router-dom";
import Select from 'react-select';


import {
    formatDateWithTime
} from '../../../../utils/dateUtils';
import {
    getToken
} from "../../../../utils/auth";
import {normalizeVietnamese} from "../../../../utils/tools";

const TableTinDang = () => {
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const navigate = useNavigate();

    // Category filter state
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [categoryError, setCategoryError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchText, setSearchText] = useState('');

    // Build react-select options from categories
    const categoryOptions = useMemo(() => {
        const opts = (categories || [])
            .filter(c => !c.isDeleted)
            .map(cat => ({value: String(cat.rowID), label: cat.name}));
        return [{value: 'all', label: 'Tất cả các loại'}, ...opts];
    }, [categories]);

    // Helper to get Authorization headers
    const getAuthHeaders = () => {
        const token = getToken();
        return token ? {'Authorization': `Bearer ${token}`} : {};
    };

    // Fetch categories on mount
    useEffect(() => {
        async function fetchCategories() {
            setLoadingCategories(true);
            setCategoryError(null);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/topic/get-topic-categories`, {
                    headers: {
                        ...getAuthHeaders(),
                    },
                });
                if (!response.ok) {
                    const text = await response.text().catch(() => null);
                    setCategoryError(text || "Failed to fetch categories");
                    return;
                }
                const data = await response.json();
                setCategories(data);
            } catch (err) {
                setCategoryError(err.message);
            } finally {
                setLoadingCategories(false);
            }
        }

        fetchCategories();
    }, []);

    // Fetch data on mount
    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_BASE_URL}/topic/get-data`, {
            headers: {
                ...getAuthHeaders(),
            },
        })
            .then((res) => res.json())
            .then((result) => {
                setData(result);
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            });
    }, []);

    // Filter data by selected category
    //const filteredData = selectedCategory === 'all' ? data : data.filter(item => String(item.rowIDTopicCategory) === String(selectedCategory));

    // // Calculate total pages
    // const totalItems = filteredData.length;
    // const totalPages = Math.ceil(totalItems / itemsPerPage);
    //
    // // Get paginated data
    // const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);


    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Handler for Add New
    const handleAddNew = () => {
        navigate("/tuvien/suatindang", {
            state: {
                row: null,
                mode: "add"
            }
        });
    };

    // Handler for Edit
    const handleEdit = (row) => {
        navigate("/tuvien/suatindang", {
            state: {
                row,
                mode: "edit"
            }
        });
    };

    // Handler for Delete/Restore
    const handleDeleteOrRestore = async (row) => {
        const isDelete = !row.isDelete;
        const actionText = isDelete ? 'Delete' : 'Restore';

        const objUpdate = {
            rowId: row.rowID,
            isDelete: isDelete
        }

        if (window.confirm(`${actionText} RowId: ${row.rowID}?`)) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/topic/set-delete-status`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...getAuthHeaders(),
                    },
                    body: JSON.stringify(objUpdate),
                });
                if (response.ok) {
                    setData(prev => prev.map(item => item.rowID === row.rowID ? {
                        ...item,
                        isDelete
                    } : item));
                } else {
                    alert(`Failed to ${actionText.toLowerCase()} topic.`);
                }
            } catch (error) {
                alert(`Error ${actionText.toLowerCase()}ing topic.`);
            }
        }
    };

    const [pageInput, setPageInput] = useState(currentPage);
    useEffect(() => {
        setPageInput(currentPage);
    }, [currentPage]);

    const handleInputChange = (e) => {
        setPageInput(e.target.value.replace(/\D/, "")); // Only allow numbers
    };

    const handleInputBlur = () => {
        let page = parseInt(pageInput, 10);
        if (!page || page < 1) page = 1;
        if (page > totalPages) page = totalPages;
        setCurrentPage(page);
    };

    const handleInputKeyDown = (e) => {
        if (e.key === "Enter") {
            handleInputBlur();
        }
    };

    // Reset to page 1 when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCategory, searchText]);


// Filter data by selected category and search text
    const filteredDataForTextSearchBox = useMemo(() => {
        let filtered = selectedCategory === 'all'
            ? data
            : data.filter(item => String(item.rowIDTopicCategory) === String(selectedCategory));

        if (searchText.trim()) {
            const normalizedSearch = normalizeVietnamese(searchText);
            filtered = filtered.filter(item => {
                const titleMatch = normalizeVietnamese(item.title).includes(normalizedSearch);
                const emailMatch = normalizeVietnamese(item.email).includes(normalizedSearch);
                const rowIdMatch = String(item.rowID).includes(searchText.trim());
                return titleMatch || emailMatch || rowIdMatch;
            });
        }

        return filtered;
    }, [data, selectedCategory, searchText]);


    // Calculate total pages using search-filtered data
    const totalItems = filteredDataForTextSearchBox.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

// Get paginated data from search-filtered results
    const paginatedData = filteredDataForTextSearchBox.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <>
            <Card
                className="bg-white border-0 rounded-3 mb-4">
                <Card.Body
                    className="p-0">
                    <div
                        className="p-4">
                        <div
                            className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                            <h3 className="mb-0">Các
                                tin
                                đăng</h3>
                            <button
                                className="btn btn-primary"
                                onClick={handleAddNew}>Thêm
                                mới
                            </button>
                        </div>
                    </div>
                    <div className="container">
                        <div className="row">
                            {/* Category filter */}
                            <div
                                className="px-4 pb-2 col-3">
                                <Form.Group
                                    className="mb-4">
                                    <label
                                        className="label text-secondary">Danh
                                        mục</label>
                                    {loadingCategories ? (
                                        <div>Tải
                                            danh
                                            mục...</div>
                                    ) : categoryError ? (
                                        <div
                                            className="text-danger">{categoryError}</div>
                                    ) : (
                                        // Use react-select instead of native select
                                        <Select
                                            options={categoryOptions}
                                            value={categoryOptions.find(o => o.value === String(selectedCategory)) || null}
                                            onChange={(opt) => setSelectedCategory(opt?.value ?? 'all')}
                                            classNamePrefix="select"
                                            placeholder="Tất cả các loại"
                                            isSearchable
                                            isClearable={false}
                                        />


                                    )}
                                </Form.Group>
                            </div>

                            {/* Search box */}
                            <div className="px-4 pb-2 col-3">
                                <Form.Group className="mb-4">
                                    <label className="label text-secondary">Tìm kiếm</label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Tìm kiếm theo tên"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        className="form-control"
                                    />
                                    {searchText && (
                                        <small className="text-muted">
                                            Tìm thấy {filteredDataForTextSearchBox.length} kết quả
                                        </small>
                                    )}
                                </Form.Group>
                            </div>
                        </div>
                    </div>
                    <div
                        className="default-table-area style-two all-projects">
                        <div
                            className="table-responsive">
                            <Table
                                className="align-middle">
                                <thead>
                                <tr>
                                    <th scope="col">RowId</th>
                                    <th scope="col">Tiêu
                                        đề
                                    </th>
                                    <th scope="col">Ngày
                                        tạo
                                    </th>
                                    <th scope="col">Người
                                        tạo
                                    </th>
                                    <th scope="col"></th>
                                    <th scope="col"></th>
                                </tr>
                                </thead>
                                <tbody>
                                {paginatedData.map((value, i) => (
                                    <tr key={i}>
                                        <td className="text-body">{value.rowID}</td>
                                        <td>{value.title}</td>
                                        <td className="text-body">{formatDateWithTime(value.createdDate)}</td>
                                        <td className="text-body">{value.email}</td>
                                        <td>
                                            <button
                                                className="btn btn-sm btn-warning me-2"
                                                onClick={() => handleEdit(value)}>Sửa
                                            </button>
                                            <button
                                                className={`btn btn-sm ${value.isDelete ? 'btn-success' : 'btn-danger'}`}
                                                onClick={() => handleDeleteOrRestore(value)}>
                                                {value.isDelete ? 'Mở' : 'Xóa'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </div>
                        <div
                            className="p-4 pt-lg-4">
                            <div
                                className="d-flex flex-column align-items-center text-center gap-2 showing-wrap">
                                <span
                                    className="fs-12 fw-medium mb-2">
                                    Showing {paginatedData.length} of {totalItems} Results
                                </span>
                                <nav
                                    aria-label="Page navigation example">
                                    <ul className="pagination mb-0 justify-content-center align-items-center gap-2">
                                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                            <button
                                                className="page-link icon"
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            >
                                                <span
                                                    className="material-symbols-outlined">keyboard_arrow_left</span>
                                            </button>
                                        </li>
                                        <li className="page-item d-flex align-items-center">
                                            <input
                                                type="text"
                                                value={pageInput}
                                                onChange={handleInputChange}
                                                onBlur={handleInputBlur}
                                                onKeyDown={handleInputKeyDown}
                                                style={{
                                                    width: "40px",
                                                    height: "32px",
                                                    textAlign: "center",
                                                    border: "1px solid #ced4da",
                                                    borderRadius: "4px",
                                                    fontSize: "14px",
                                                    padding: "2px 6px"
                                                }}
                                                className="form-control d-inline-block mx-2"
                                            />
                                            <span
                                                style={{
                                                    fontSize: "14px",
                                                    marginLeft: "4px"
                                                }}> / {totalPages}</span>
                                        </li>
                                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                            <button
                                                className="page-link icon"
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                            >
                                                <span
                                                    className="material-symbols-outlined">keyboard_arrow_right</span>
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </>
    );
};

export default TableTinDang;
